import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Fab from '@mui/material/Fab'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import DownloadIcon from '@mui/icons-material/Download'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import MenuIcon from '@mui/icons-material/Menu'
import { Link } from '@inertiajs/react'
import { LogEntryComponent, SessionSummaryCard } from '@/components/agents/session_log_entries'
import type { LogEntry, SessionSummary } from '@/components/agents/session_log_entries'
import { useSessionLogChannel } from '@/hooks/useSessionLogChannel'

interface Agent {
  id: number
  username: string
  slug: string
}

interface Task {
  id: number
  summary: string
  board_id: number
  web_url?: string
}

export interface SessionDetailData {
  id: number
  session_id: string
  model: string | null
  claude_code_version: string | null
  started_at: string
  message_count: number
  status: string
  agent: Agent
  task: Task | null
  metadata?: Record<string, unknown> | null
  filename?: string
  byte_size?: number
  download_url?: string
  session_summary: SessionSummary | null
  parsed_entries: LogEntry[]
}

interface Props {
  session: SessionDetailData
  onMenuClick?: () => void
  scrollToEntry?: number | null
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function buildStructuredEntries(
  sequence: number,
  entryType: string,
  data: Record<string, unknown>
): LogEntry[] {
  let idx = sequence
  switch (entryType) {
    case 'system':
      return [{
        index: idx,
        type: 'system',
        subtype: data.subtype as string | undefined,
        session_id: data.session_id as string | undefined,
        model: data.model as string | undefined,
        tools: data.tools as string[] | undefined,
        agents: data.agents as string[] | undefined,
        cwd: data.cwd as string | undefined,
        permission_mode: data.permissionMode as string | undefined,
      }]
    case 'assistant': {
      const msg = (data.message || {}) as Record<string, unknown>
      const usage = msg.usage as { input_tokens: number; output_tokens: number } | undefined
      const content = Array.isArray(msg.content) ? msg.content : []
      const textParts = content.filter((p: Record<string, unknown>) => p.type === 'text')
      const toolUseParts = content.filter((p: Record<string, unknown>) => p.type === 'tool_use')
      const result: LogEntry[] = []

      if (textParts.length > 0) {
        result.push({
          index: idx++,
          type: 'assistant_text',
          text: textParts.map((p: Record<string, unknown>) => p.text).join('\n\n'),
          usage: usage ? { input_tokens: usage.input_tokens, output_tokens: usage.output_tokens } : null,
        })
      }

      for (const part of toolUseParts) {
        result.push({
          index: idx++,
          type: 'tool_call',
          name: part.name as string,
          tool_use_id: part.id as string,
          input: (part.input as Record<string, unknown>) ?? null,
          result: null,
          is_error: false,
        })
      }

      return result
    }
    case 'user': {
      const msg = (data.message || {}) as Record<string, unknown>
      const content = Array.isArray(msg.content) ? msg.content : []
      const textParts = content.filter((p: Record<string, unknown>) => p.type === 'text')
      const toolResultParts = content.filter((p: Record<string, unknown>) => p.type === 'tool_result')
      const result: LogEntry[] = []

      for (const part of toolResultParts) {
        const resultContent = part.content
        const resultText = typeof resultContent === 'string'
          ? resultContent
          : Array.isArray(resultContent)
            ? (resultContent as Record<string, unknown>[]).map((c) => typeof c === 'string' ? c : String(c.text ?? '')).join('\n')
            : String(resultContent ?? '')

        result.push({
          index: idx++,
          type: 'tool_call',
          name: 'unknown',
          tool_use_id: part.tool_use_id as string,
          input: null,
          result: resultText.slice(0, 30_000),
          is_error: part.is_error === true,
        })
      }

      if (textParts.length > 0) {
        result.push({
          index: idx++,
          type: 'user_text',
          text: textParts.map((p: Record<string, unknown>) => p.text).join('\n\n'),
        })
      }

      return result
    }
    case 'result':
      return []
    default:
      return [{
        index: idx,
        type: 'unknown',
        raw: JSON.stringify(data).slice(0, 500),
      }]
  }
}

const INITIAL_ENTRIES = 50
const LOAD_MORE_SIZE = 50

export function SessionDetail({ session, onMenuClick, scrollToEntry }: Props) {
  const [entries, setEntries] = useState<LogEntry[]>(session.parsed_entries)
  const [status, setStatus] = useState(session.status)
  const [summary, setSummary] = useState<SessionSummary | null>(session.session_summary)
  const [autoScroll, setAutoScroll] = useState(true)
  const [highlightedEntry, setHighlightedEntry] = useState<number | null>(null)
  const [visibleStartIndex, setVisibleStartIndex] = useState(
    () => Math.max(0, session.parsed_entries.length - INITIAL_ENTRIES)
  )
  const bottomRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  // Tracks scroll height before prepending entries so we can restore position seamlessly
  const prevScrollHeightRef = useRef<number | null>(null)
  // Ref for use inside scroll handler closure (avoids stale closure)
  const visibleStartRef = useRef(visibleStartIndex)
  visibleStartRef.current = visibleStartIndex

  const isStreaming = status === 'streaming'

  // Reset state when selected session changes
  useEffect(() => {
    const newEntries = session.parsed_entries
    setEntries(newEntries)
    setStatus(session.status)
    setSummary(session.session_summary)
    setAutoScroll(scrollToEntry == null)
    if (scrollToEntry != null) {
      const entryPos = newEntries.findIndex((e) => e.index === scrollToEntry)
      setVisibleStartIndex(entryPos >= 0 ? Math.max(0, entryPos - 25) : Math.max(0, newEntries.length - INITIAL_ENTRIES))
    } else {
      setVisibleStartIndex(Math.max(0, newEntries.length - INITIAL_ENTRIES))
    }
  }, [session.id])

  // Scroll to specific entry when scrollToEntry is set
  useEffect(() => {
    if (scrollToEntry == null) return
    // Wait for entries to render
    const timer = setTimeout(() => {
      const el = document.getElementById(`entry-${scrollToEntry}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setHighlightedEntry(scrollToEntry)
        // Clear highlight after animation
        setTimeout(() => setHighlightedEntry(null), 2500)
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [scrollToEntry, session.id])

  const handleEntry = useCallback((rawEntry: { sequence: number; entry_type: string; data: Record<string, unknown> }) => {
    if (rawEntry.entry_type === 'result') {
      const data = rawEntry.data
      setSummary({
        duration_ms: (data.duration_ms as number) ?? null,
        cost_usd: (data.cost_usd as number) ?? null,
        is_error: data.is_error === true,
        result: (data.result as string) ?? null,
        total_input_tokens: ((data.usage as Record<string, number>)?.input_tokens) ?? 0,
        total_output_tokens: ((data.usage as Record<string, number>)?.output_tokens) ?? 0,
      })
      return
    }

    const structured = buildStructuredEntries(rawEntry.sequence, rawEntry.entry_type, rawEntry.data)
    if (structured.length > 0) {
      setEntries((prev) => [...prev, ...structured])
    }
  }, [])

  const handleFinished = useCallback((newStatus: string) => {
    setStatus(newStatus)
  }, [])

  useSessionLogChannel(session.id, isStreaming, handleEntry, handleFinished)

  // Scroll to bottom on mount and session change (before browser paints, so user never sees top)
  useLayoutEffect(() => {
    if (scrollToEntry != null) return
    const container = scrollContainerRef.current
    if (container) {
      container.scrollTop = container.scrollHeight
    }
  }, [session.id])

  // Restore scroll position after prepending entries (runs synchronously before browser paint)
  useLayoutEffect(() => {
    if (prevScrollHeightRef.current === null) return
    const container = scrollContainerRef.current
    if (!container) return
    container.scrollTop += container.scrollHeight - prevScrollHeightRef.current
    prevScrollHeightRef.current = null
  }, [visibleStartIndex])

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [entries, autoScroll])

  // Detect scroll position: auto-scroll tracking + load older entries when near top
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => {
      const scrollBottom = container.scrollTop + container.clientHeight
      const scrollHeight = container.scrollHeight
      setAutoScroll(scrollHeight - scrollBottom < 200)

      // Load older entries when scrolled near the top
      if (container.scrollTop <= 100 && prevScrollHeightRef.current === null && visibleStartRef.current > 0) {
        prevScrollHeightRef.current = container.scrollHeight
        setVisibleStartIndex((prev) => Math.max(0, prev - LOAD_MORE_SIZE))
      }
    }
    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'background.paper',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {onMenuClick && (
            <IconButton onClick={onMenuClick} edge="start" sx={{ minWidth: 44, minHeight: 44 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3" sx={{ fontSize: '1rem', fontWeight: 700 }} noWrap>
              @{session.agent.username}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {session.session_id.slice(0, 12)}... &middot; {session.model || 'Unknown model'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Chip
            icon={isStreaming ? (
              <FiberManualRecordIcon sx={{ fontSize: 10, '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } }, animation: 'pulse 1.5s ease-in-out infinite' }} />
            ) : undefined}
            label={isStreaming ? 'Live' : status === 'completed' ? 'Completed' : status === 'aborted' ? 'Aborted' : status === 'expired' ? 'Expired' : status === 'failed' ? 'Failed' : status}
            color={isStreaming ? 'success' : status === 'completed' ? 'default' : status === 'aborted' ? 'warning' : status === 'failed' ? 'error' : 'default'}
            size="small"
            variant="outlined"
          />
          {session.download_url && session.byte_size && (
            <Button
              component="a"
              href={session.download_url}
              startIcon={<DownloadIcon />}
              size="small"
              variant="outlined"
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              {formatBytes(session.byte_size)}
            </Button>
          )}
        </Box>
      </Box>

      {/* Scrollable content area */}
      <Box ref={scrollContainerRef} data-testid="scroll-container" sx={{ flex: 1, overflow: 'auto', px: { xs: 2, md: 3 }, py: 2 }}>
        {/* Session details grid */}
        <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 1.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Session ID</Typography>
              <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">{session.session_id}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Agent</Typography>
              <Typography variant="body2">{session.agent.username}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Model</Typography>
              <Typography variant="body2">{session.model || '-'}</Typography>
            </Box>
            {session.claude_code_version && (
              <Box>
                <Typography variant="caption" color="text.secondary">Version</Typography>
                <Typography variant="body2">{session.claude_code_version}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">Started</Typography>
              <Typography variant="body2">{new Date(session.started_at).toLocaleString()}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Entries</Typography>
              <Typography variant="body2">{entries.length}</Typography>
            </Box>
            {session.task && (
              <Box>
                <Typography variant="caption" color="text.secondary">Task</Typography>
                <Typography variant="body2">
                  <Link href={session.task.web_url || `/tasks/boards/${session.task.board_id}/tasks/${session.task.id}`}>
                    {session.task.summary}
                  </Link>
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {summary && <SessionSummaryCard summary={summary} />}

        {/* Log entries */}
        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {isStreaming ? 'Waiting for entries...' : 'No entries to display.'}
          </Typography>
        ) : (
          <Box>
            {entries.slice(visibleStartIndex).map((entry) => (
              <LogEntryComponent key={entry.index} entry={entry} highlighted={entry.index === highlightedEntry} />
            ))}
          </Box>
        )}
        <div ref={bottomRef} />
      </Box>

      {/* Scroll-to-bottom FAB for streaming sessions */}
      {!autoScroll && isStreaming && (
        <Fab
          size="small"
          color="primary"
          sx={{ position: 'absolute', bottom: 24, right: 24 }}
          onClick={() => {
            setAutoScroll(true)
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
          }}
        >
          <KeyboardArrowDownIcon />
        </Fab>
      )}
    </Box>
  )
}
